import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  ArrowUpward as ArrowUpIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  EmojiEvents as ObjectiveIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import objectiveService from '../../services/objective.service';
import objectiveContainerService from '../../services/objectiveContainerService';
import skillService from '../../services/skill.service';
import courseService from '../../services/course.service';
import patternService from '../../services/pattern.service';
import { registerBeforeTabChange, unregisterBeforeTabChange } from './Workshop';

interface ObjectivesProps {
  urlContainerId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Objectives: React.FC<ObjectivesProps> = ({ urlContainerId, onNavChange }) => {
  const [objectives, setObjectives] = useState<any[]>([]);
  const [containers, setContainers] = useState<any[]>([]);
  const [selectedObjective, setSelectedObjective] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentContainer, setCurrentContainer] = useState<number | null>(null);
  const [parentContainer, setParentContainer] = useState<number | null>(null);

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    if (urlContainerId !== undefined && urlContainerId !== currentContainer) {
      setCurrentContainer(urlContainerId);
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [objectiveType, setObjectiveType] = useState<'skill' | 'course' | 'exercise_pattern'>('skill');
  const [skillId, setSkillId] = useState<number | null>(null);
  const [courseId, setCourseId] = useState<number | null>(null);
  const [exercisePatternId, setExercisePatternId] = useState<number | null>(null);
  const [weight, setWeight] = useState(1);

  // Lists for selection
  const [skills, setSkills] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [exercisePatterns, setExercisePatterns] = useState<any[]>([]);

  // Ref to track current values for saving on unmount
  const currentObjectiveRef = useRef<{
    objective: any | null;
    description: string;
    objectiveType: 'skill' | 'course' | 'exercise_pattern';
    skillId: number | null;
    courseId: number | null;
    exercisePatternId: number | null;
    weight: number;
  }>({
    objective: null,
    description: '',
    objectiveType: 'skill',
    skillId: null,
    courseId: null,
    exercisePatternId: null,
    weight: 1
  });

  // Update ref whenever values change
  useEffect(() => {
    currentObjectiveRef.current = {
      objective: selectedObjective,
      description,
      objectiveType,
      skillId,
      courseId,
      exercisePatternId,
      weight
    };
  }, [selectedObjective, description, objectiveType, skillId, courseId, exercisePatternId, weight]);

  // Creation dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'container' | 'objective'>('objective');
  const [newContainerTitle, setNewContainerTitle] = useState('');
  const [newObjectiveTitle, setNewObjectiveTitle] = useState('');
  const [newObjectiveType, setNewObjectiveType] = useState<'skill' | 'course' | 'exercise_pattern'>('skill');
  const [newObjectiveSkillId, setNewObjectiveSkillId] = useState<number | null>(null);
  const [newObjectiveCourseId, setNewObjectiveCourseId] = useState<number | null>(null);
  const [newObjectivePatternId, setNewObjectivePatternId] = useState<number | null>(null);
  const [autoGeneratedTitle, setAutoGeneratedTitle] = useState(true);
  const [newContainerWeight, setNewContainerWeight] = useState(1);
  const [newObjectiveWeight, setNewObjectiveWeight] = useState(1);

  // Edit container dialog
  const [editContainerDialogOpen, setEditContainerDialogOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<any | null>(null);
  const [editContainerTitle, setEditContainerTitle] = useState('');
  const [editContainerDescription, setEditContainerDescription] = useState('');

  // Edit objective dialog
  const [editObjectiveDialogOpen, setEditObjectiveDialogOpen] = useState(false);
  const [editingObjective, setEditingObjective] = useState<any | null>(null);
  const [editObjectiveTitle, setEditObjectiveTitle] = useState('');
  const [editContainerWeight, setEditContainerWeight] = useState(1);

  // Load selection lists
  useEffect(() => {
    const loadLists = async () => {
      try {
        const [skillsData, coursesData, patternsData] = await Promise.all([
          skillService.getAll(),
          courseService.getCourses(),
          patternService.getAllPatterns(),
        ]);
        setSkills(skillsData);
        setCourses(coursesData);
        setExercisePatterns(patternsData);
      } catch (error) {
        console.error('Error loading lists:', error);
      }
    };
    loadLists();
  }, []);

  // Auto-generate title when type and reference change
  useEffect(() => {
    if (!autoGeneratedTitle) return;

    let generatedTitle = '';

    if (newObjectiveType === 'skill' && newObjectiveSkillId) {
      const skill = skills.find(s => s.id === newObjectiveSkillId);
      if (skill) {
        generatedTitle = `Savoir-faire : ${skill.name}`;
      }
    } else if (newObjectiveType === 'course' && newObjectiveCourseId) {
      const course = courses.find(c => c.id === newObjectiveCourseId);
      if (course) {
        generatedTitle = `Savoir démontrer : ${course.title}`;
      }
    } else if (newObjectiveType === 'exercise_pattern' && newObjectivePatternId) {
      const pattern = exercisePatterns.find(p => p.id === newObjectivePatternId);
      if (pattern) {
        generatedTitle = `Savoir faire un exercice : ${pattern.title}`;
      }
    }

    if (generatedTitle) {
      setNewObjectiveTitle(generatedTitle);
    }
  }, [newObjectiveType, newObjectiveSkillId, newObjectiveCourseId, newObjectivePatternId, skills, courses, exercisePatterns, autoGeneratedTitle]);

  const initialLoadDone = useRef(false);

  const loadObjectives = useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const params: any = {};
      if (currentContainer === null) {
        params.container = 'null';
      } else if (currentContainer) {
        params.container = currentContainer;
      }

      const data = await objectiveService.getAll(params);
      setObjectives(data);
    } catch (error) {
      console.error('Error loading objectives:', error);
      toast.error('Erreur lors du chargement des objectifs');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [currentContainer]);

  const loadContainers = useCallback(async () => {
    try {
      const data = await objectiveContainerService.getContainers(currentContainer);
      setContainers(data);

      // Load parent info if we're inside a container
      if (currentContainer !== null) {
        const containerInfo = await objectiveContainerService.getContainer(currentContainer);
        setParentContainer(containerInfo.parent);
      } else {
        setParentContainer(null);
      }
    } catch (error) {
      console.error('Error loading containers:', error);
    }
  }, [currentContainer]);

  useEffect(() => {
    loadObjectives();
    loadContainers();
  }, [currentContainer, loadObjectives, loadContainers]);

  // Auto-save description with 2 second delay
  useEffect(() => {
    if (!selectedObjective || !title) return;

    const timeoutId = setTimeout(async () => {
      try {
        const objectiveData: any = {
          title,
          description,
          objective_type: objectiveType,
          container: currentContainer,
          weight,
          tags: [],
          skill: objectiveType === 'skill' ? skillId : null,
          course: objectiveType === 'course' ? courseId : null,
          exercise_pattern: objectiveType === 'exercise_pattern' ? exercisePatternId : null,
        };

        await objectiveService.update(selectedObjective.id, objectiveData);

        // Update selected objective with saved values
        const updatedObjective = {
          ...selectedObjective,
          description,
          objective_type: objectiveType,
          weight,
          skill: objectiveType === 'skill' ? skillId : null,
          course: objectiveType === 'course' ? courseId : null,
          exercise_pattern: objectiveType === 'exercise_pattern' ? exercisePatternId : null,
        };
        setSelectedObjective(updatedObjective);

        // Update objective in the list
        setObjectives(prevObjectives => prevObjectives.map(o =>
          o.id === selectedObjective.id ? updatedObjective : o
        ));

        console.log('Objective auto-saved (description)');
      } catch (error) {
        console.error('Error auto-saving objective:', error);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [description, selectedObjective, title, objectiveType, currentContainer, weight, skillId, courseId, exercisePatternId]);

  // Auto-save immediately when type, reference, or weight changes
  useEffect(() => {
    if (!selectedObjective || !title) return;

    // Skip on initial load
    if (description === selectedObjective.description &&
        objectiveType === selectedObjective.objective_type &&
        weight === (selectedObjective.weight || 1) &&
        skillId === selectedObjective.skill &&
        courseId === selectedObjective.course &&
        exercisePatternId === selectedObjective.exercise_pattern) {
      return;
    }

    const saveImmediately = async () => {
      try {
        const objectiveData: any = {
          title,
          description,
          objective_type: objectiveType,
          container: currentContainer,
          weight,
          tags: [],
          skill: objectiveType === 'skill' ? skillId : null,
          course: objectiveType === 'course' ? courseId : null,
          exercise_pattern: objectiveType === 'exercise_pattern' ? exercisePatternId : null,
        };

        await objectiveService.update(selectedObjective.id, objectiveData);

        // Update selected objective with saved values
        const updatedObjective = {
          ...selectedObjective,
          description,
          objective_type: objectiveType,
          weight,
          skill: objectiveType === 'skill' ? skillId : null,
          course: objectiveType === 'course' ? courseId : null,
          exercise_pattern: objectiveType === 'exercise_pattern' ? exercisePatternId : null,
        };
        setSelectedObjective(updatedObjective);

        // Update objective in the list
        setObjectives(prevObjectives => prevObjectives.map(o =>
          o.id === selectedObjective.id ? updatedObjective : o
        ));

        console.log('Objective auto-saved (immediate)');
      } catch (error) {
        console.error('Error auto-saving objective:', error);
      }
    };

    saveImmediately();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectiveType, skillId, courseId, exercisePatternId, weight]);

  // Register callback to save before tab change
  useEffect(() => {
    const saveBeforeTabChange = async () => {
      const current = currentObjectiveRef.current;
      if (current.objective && (
          current.description !== current.objective.description ||
          current.objectiveType !== current.objective.objective_type ||
          current.weight !== (current.objective.weight || 1) ||
          current.skillId !== current.objective.skill ||
          current.courseId !== current.objective.course ||
          current.exercisePatternId !== current.objective.exercise_pattern
      )) {
        try {
          const objectiveData: any = {
            title: current.objective.title,
            description: current.description,
            objective_type: current.objectiveType,
            container: current.objective.container,
            weight: current.weight,
            tags: [],
            skill: current.objectiveType === 'skill' ? current.skillId : null,
            course: current.objectiveType === 'course' ? current.courseId : null,
            exercise_pattern: current.objectiveType === 'exercise_pattern' ? current.exercisePatternId : null,
          };
          await objectiveService.update(current.objective.id, objectiveData);
          console.log('Objective saved before tab change');
        } catch (error) {
          console.error('Error saving objective before tab change:', error);
        }
      }
    };

    registerBeforeTabChange('objectives', saveBeforeTabChange);

    return () => {
      unregisterBeforeTabChange('objectives');
    };
  }, []); // Empty dependency array - only register/unregister on mount/unmount

  // Save on component unmount (for navigation outside Workshop)
  useEffect(() => {
    return () => {
      const current = currentObjectiveRef.current;
      if (current.objective && (
          current.description !== current.objective.description ||
          current.objectiveType !== current.objective.objective_type ||
          current.weight !== (current.objective.weight || 1) ||
          current.skillId !== current.objective.skill ||
          current.courseId !== current.objective.course ||
          current.exercisePatternId !== current.objective.exercise_pattern
      )) {
        // Fire off the save request without awaiting
        const objectiveData: any = {
          title: current.objective.title,
          description: current.description,
          objective_type: current.objectiveType,
          container: current.objective.container,
          weight: current.weight,
          tags: [],
          skill: current.objectiveType === 'skill' ? current.skillId : null,
          course: current.objectiveType === 'course' ? current.courseId : null,
          exercise_pattern: current.objectiveType === 'exercise_pattern' ? current.exercisePatternId : null,
        };
        objectiveService.update(current.objective.id, objectiveData)
          .then(() => console.log('Objective saved on unmount'))
          .catch(err => console.error('Error saving objective on unmount:', err));
      }
    };
  }, []); // Empty dependency array - only run on unmount

  const handleSelectObjective = async (objective: any) => {
    // Save current objective before switching if there are changes
    if (selectedObjective && title && (
        description !== selectedObjective.description ||
        objectiveType !== selectedObjective.objective_type ||
        weight !== (selectedObjective.weight || 1) ||
        skillId !== selectedObjective.skill ||
        courseId !== selectedObjective.course ||
        exercisePatternId !== selectedObjective.exercise_pattern
    )) {
      try {
        const objectiveData: any = {
          title,
          description,
          objective_type: objectiveType,
          container: currentContainer,
          weight,
          tags: [],
          skill: objectiveType === 'skill' ? skillId : null,
          course: objectiveType === 'course' ? courseId : null,
          exercise_pattern: objectiveType === 'exercise_pattern' ? exercisePatternId : null,
        };
        await objectiveService.update(selectedObjective.id, objectiveData);

        // Update selected objective with saved values
        const updatedObjective = {
          ...selectedObjective,
          description,
          objective_type: objectiveType,
          weight,
          skill: objectiveType === 'skill' ? skillId : null,
          course: objectiveType === 'course' ? courseId : null,
          exercise_pattern: objectiveType === 'exercise_pattern' ? exercisePatternId : null,
        };
        setSelectedObjective(updatedObjective);

        // Update objective in the list
        setObjectives(prevObjectives => prevObjectives.map(o =>
          o.id === selectedObjective.id ? updatedObjective : o
        ));

        console.log('Saved objective before switching');
      } catch (error) {
        console.error('Error saving objective:', error);
      }
    }

    setSelectedObjective(objective);
    setTitle(objective.title);
    setDescription(objective.description);
    setObjectiveType(objective.objective_type);
    setSkillId(objective.skill);
    setCourseId(objective.course);
    setExercisePatternId(objective.exercise_pattern);
    setWeight(objective.weight || 1);
  };

  const handleNavigateToContainer = async (containerId: number | null) => {
    // Save current objective before navigating if there are changes
    if (selectedObjective && title && (
        description !== selectedObjective.description ||
        objectiveType !== selectedObjective.objective_type ||
        weight !== (selectedObjective.weight || 1) ||
        skillId !== selectedObjective.skill ||
        courseId !== selectedObjective.course ||
        exercisePatternId !== selectedObjective.exercise_pattern
    )) {
      try {
        const objectiveData: any = {
          title,
          description,
          objective_type: objectiveType,
          container: currentContainer,
          weight,
          tags: [],
          skill: objectiveType === 'skill' ? skillId : null,
          course: objectiveType === 'course' ? courseId : null,
          exercise_pattern: objectiveType === 'exercise_pattern' ? exercisePatternId : null,
        };
        await objectiveService.update(selectedObjective.id, objectiveData);

        // Update selected objective with saved values
        const updatedObjective = {
          ...selectedObjective,
          description,
          objective_type: objectiveType,
          weight,
          skill: objectiveType === 'skill' ? skillId : null,
          course: objectiveType === 'course' ? courseId : null,
          exercise_pattern: objectiveType === 'exercise_pattern' ? exercisePatternId : null,
        };
        setSelectedObjective(updatedObjective);

        // Update objective in the list
        setObjectives(prevObjectives => prevObjectives.map(o =>
          o.id === selectedObjective.id ? updatedObjective : o
        ));

        console.log('Saved objective before navigating');
      } catch (error) {
        console.error('Error saving objective:', error);
      }
    }

    setCurrentContainer(containerId);
    const container = containers.find(c => c.id === containerId);
    onNavChange?.(containerId, null, container?.title);
  };

  const handleDelete = async (objectiveId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet objectif ?')) {
      return;
    }

    try {
      await objectiveService.delete(objectiveId);
      toast.success('Objectif supprimé');
      resetForm();
      loadObjectives();
    } catch (error) {
      console.error('Error deleting objective:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublicObjective = async (objectiveId: number, newIsPublic: boolean) => {
    try {
      await objectiveService.update(objectiveId, { is_public: newIsPublic });
      toast.success(`Objectif rendu ${newIsPublic ? 'public' : 'privé'}`);

      // Update in the list
      setObjectives(objectives.map(o => o.id === objectiveId ? { ...o, is_public: newIsPublic } : o));

      // Update selected objective if it's the same
      if (selectedObjective?.id === objectiveId) {
        setSelectedObjective({ ...selectedObjective, is_public: newIsPublic });
      }
    } catch (error) {
      console.error('Error updating objective visibility:', error);
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleNew = () => {
    setDialogOpen(true);
    setCreateType('objective');
    setAutoGeneratedTitle(true);
  };

  const handleCreateContainer = async () => {
    if (!newContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await objectiveContainerService.createContainer({
        title: newContainerTitle,
        parent: currentContainer,
        weight: newContainerWeight,
      });
      toast.success('Contenant créé');
      setDialogOpen(false);
      setNewContainerTitle('');
      setNewContainerWeight(1);
      loadContainers();
    } catch (error) {
      console.error('Error creating container:', error);
      toast.error('Erreur lors de la création du contenant');
    }
  };

  const handleCreateNewObjective = async () => {
    if (!newObjectiveTitle.trim()) {
      toast.error('Le titre est requis');
      return;
    }

    // Validate that appropriate field is set based on type
    if (newObjectiveType === 'skill' && !newObjectiveSkillId) {
      toast.error('Veuillez sélectionner un savoir-faire');
      return;
    }
    if (newObjectiveType === 'course' && !newObjectiveCourseId) {
      toast.error('Veuillez sélectionner un cours');
      return;
    }
    if (newObjectiveType === 'exercise_pattern' && !newObjectivePatternId) {
      toast.error("Veuillez sélectionner un pattern d'exercice");
      return;
    }

    try {
      const newObjective = await objectiveService.create({
        title: newObjectiveTitle,
        description: 'À compléter',
        objective_type: newObjectiveType,
        container: currentContainer,
        weight: newObjectiveWeight,
        tags: [],
        skill: newObjectiveType === 'skill' ? newObjectiveSkillId : null,
        course: newObjectiveType === 'course' ? newObjectiveCourseId : null,
        exercise_pattern: newObjectiveType === 'exercise_pattern' ? newObjectivePatternId : null,
      });
      toast.success('Objectif créé');
      setDialogOpen(false);
      setNewObjectiveTitle('');
      setNewObjectiveType('skill');
      setNewObjectiveSkillId(null);
      setNewObjectiveCourseId(null);
      setNewObjectivePatternId(null);
      setNewObjectiveWeight(1);
      setAutoGeneratedTitle(true);
      setSelectedObjective(newObjective);
      setTitle(newObjective.title);
      setDescription(newObjective.description);
      setObjectiveType(newObjective.objective_type);
      setSkillId(newObjective.skill);
      setCourseId(newObjective.course);
      setExercisePatternId(newObjective.exercise_pattern);
      setWeight(newObjective.weight || 1);
      loadObjectives();
    } catch (error) {
      console.error('Error creating objective:', error);
      toast.error("Erreur lors de la création de l'objectif");
    }
  };

  const handleEditContainer = (container: any) => {
    setEditingContainer(container);
    setEditContainerTitle(container.title);
    setEditContainerDescription(container.description || '');
    setEditContainerWeight(container.weight || 1);
    setEditContainerDialogOpen(true);
  };

  const handleEditObjective = (objective: any) => {
    setEditingObjective(objective);
    setEditObjectiveTitle(objective.title);
    setEditObjectiveDialogOpen(true);
  };

  const handleUpdateObjectiveTitle = async () => {
    if (!editObjectiveTitle.trim()) {
      toast.error('Le titre de l\'objectif est requis');
      return;
    }

    try {
      await objectiveService.update(editingObjective.id, {
        title: editObjectiveTitle,
      });
      toast.success('Objectif mis à jour');
      loadObjectives();
      setEditObjectiveDialogOpen(false);
      setEditingObjective(null);
      setEditObjectiveTitle('');

      // Update selected objective if it's the one being edited
      if (selectedObjective?.id === editingObjective.id) {
        setSelectedObjective({ ...selectedObjective, title: editObjectiveTitle });
        setTitle(editObjectiveTitle);
      }
    } catch (error) {
      console.error('Error updating objective:', error);
      toast.error('Erreur lors de la mise à jour de l\'objectif');
    }
  };

  const handleUpdateContainer = async () => {
    if (!editContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await objectiveContainerService.updateContainer(editingContainer.id, {
        title: editContainerTitle,
        description: editContainerDescription,
        weight: editContainerWeight,
      });
      toast.success('Contenant mis à jour');
      setEditContainerDialogOpen(false);
      setEditingContainer(null);
      setEditContainerTitle('');
      setEditContainerDescription('');
      setEditContainerWeight(1);
      loadContainers();
    } catch (error) {
      console.error('Error updating container:', error);
      toast.error('Erreur lors de la mise à jour du contenant');
    }
  };

  const handleDeleteContainer = async (containerId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenant ?')) {
      return;
    }

    try {
      await objectiveContainerService.deleteContainer(containerId);
      toast.success('Contenant supprimé');
      loadContainers();
    } catch (error) {
      console.error('Error deleting container:', error);
      toast.error('Erreur lors de la suppression du contenant');
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    const [itemType, itemIdStr] = draggableId.split('-');
    const itemId = parseInt(itemIdStr);

    if (source.droppableId !== destination.droppableId) {
      if (itemType === 'objective' && destination.droppableId === 'parent-container') {
        try {
          await objectiveService.update(itemId, { container: parentContainer });
          toast.success('Objectif déplacé');
          loadObjectives();
          loadContainers();
        } catch (error) {
          console.error('Error moving objective:', error);
          toast.error('Erreur lors du déplacement');
        }
        return;
      }

      // Moving objective into a container
      if (itemType === 'objective' && destination.droppableId.startsWith('container-')) {
        const targetContainerId = parseInt(destination.droppableId.split('-')[1]);
        try {
          await objectiveService.update(itemId, { container: targetContainerId });
          toast.success('Objectif déplacé');
          loadObjectives();
          loadContainers();
        } catch (error) {
          console.error('Error moving objective:', error);
          toast.error('Erreur lors du déplacement');
        }
        return;
      }

      toast.info('Déplacement non supporté');
      return;
    }

    if (itemType === 'container') {
      const items = Array.from(containers);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      setContainers(items);

      try {
        const itemsOrder = items.map((item, idx) => ({ id: item.id, order: idx }));
        await objectiveContainerService.reorderContainers(itemsOrder);
        toast.success('Ordre mis à jour');
      } catch (error) {
        console.error('Error reordering containers:', error);
        toast.error("Erreur lors de la mise à jour de l'ordre");
        loadContainers();
      }
    } else if (itemType === 'objective') {
      const items = Array.from(objectives);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      setObjectives(items);

      try {
        const itemsOrder = items.map((item, idx) => ({ id: item.id, order: idx }));
        await objectiveService.reorder(itemsOrder);
        toast.success('Ordre mis à jour');
      } catch (error) {
        console.error('Error reordering objectives:', error);
        toast.error("Erreur lors de la mise à jour de l'ordre");
        loadObjectives();
      }
    }
  };

  const resetForm = () => {
    setSelectedObjective(null);
    setTitle('');
    setDescription('');
    setObjectiveType('skill');
    setSkillId(null);
    setCourseId(null);
    setExercisePatternId(null);
    setWeight(1);
  };

  const getObjectiveTypeLabel = (type: string) => {
    switch (type) {
      case 'skill': return 'Maîtriser un savoir-faire';
      case 'course': return 'Savoir démontrer';
      case 'exercise_pattern': return 'Savoir faire un exercice';
      default: return type;
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Objectifs</Typography>
              <Button startIcon={<AddIcon />} onClick={handleNew} size="small">
                Nouveau
              </Button>
            </Box>

            <DragDropContext onDragEnd={handleDragEnd}>
              {/* Parent navigation */}
              {currentContainer !== null && (
                <Droppable droppableId="parent-container" type="OBJECTIVE">
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        mb: 1,
                        cursor: 'pointer',
                        backgroundColor: snapshot.isDraggingOver ? 'action.selected' : 'action.hover',
                        transition: 'background-color 0.2s',
                      }}
                      onClick={() => handleNavigateToContainer(parentContainer)}
                    >
                      <CardContent sx={{ py: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ArrowUpIcon color="action" />
                          <Typography variant="body2" fontWeight="bold">..</Typography>
                        </Box>
                        {provided.placeholder}
                      </CardContent>
                    </Card>
                  )}
                </Droppable>
              )}

              {/* Containers */}
              <Droppable droppableId="containers" type="CONTAINER">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {containers.map((container, index) => (
                      <Draggable key={container.id} draggableId={`container-${container.id}`} index={index}>
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} {...provided.dragHandleProps}>
                            <Droppable droppableId={`container-${container.id}`} type="OBJECTIVE">
                              {(provided2, snapshot2) => (
                                <Card
                                  ref={provided2.innerRef}
                                  {...provided2.droppableProps}
                                  sx={{
                                    mb: 1,
                                    cursor: 'pointer',
                                    border: '2px dashed',
                                    borderColor: 'primary.main',
                                    backgroundColor: snapshot.isDragging ? 'action.hover' : snapshot2.isDraggingOver ? 'action.selected' : 'inherit',
                                    transition: 'background-color 0.2s',
                                  }}
                                >
                                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <DragIcon sx={{ cursor: 'grab', color: 'action.active' }} />
                                      <Box
                                        sx={{ display: 'flex', alignItems: 'center', flex: 1, minHeight: '20px', cursor: 'pointer' }}
                                        onClick={() => handleNavigateToContainer(container.id)}
                                      >
                                        <FolderIcon color="primary" sx={{ mr: 1, flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {container.title}
                                          </Typography>
                                          {container.description && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: 'text.secondary',
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {container.description}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditContainer(container); }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteContainer(container.id); }} sx={{ color: 'error.main' }}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                    {provided2.placeholder}
                                  </CardContent>
                                </Card>
                              )}
                            </Droppable>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              {/* Objectives */}
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <Droppable droppableId="objectives" type="OBJECTIVE">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {objectives.map((objective, index) => (
                        <Draggable key={objective.id} draggableId={`objective-${objective.id}`} index={index}>
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                mb: 1,
                                cursor: 'pointer',
                                backgroundColor: selectedObjective?.id === objective.id ? 'action.selected' : snapshot.isDragging ? 'action.hover' : objective.is_public ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
                              }}
                              onClick={() => handleSelectObjective(objective)}
                            >
                              <CardContent sx={{ py: 1 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box {...provided.dragHandleProps}>
                                    <DragIcon sx={{ cursor: 'grab', color: 'action.active', fontSize: 20 }} />
                                  </Box>
                                  <FileIcon sx={{ fontSize: 20 }} />
                                  <Box flex={1}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {objective.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {getObjectiveTypeLabel(objective.objective_type)}
                                    </Typography>
                                  </Box>
                                  <Switch
                                    size="small"
                                    checked={objective.is_public || false}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleTogglePublicObjective(objective.id, e.target.checked);
                                    }}
                                    sx={{ ml: 0.5 }}
                                  />
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditObjective(objective); }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(objective.id); }} sx={{ color: 'error.main' }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
            </DragDropContext>
          </Paper>
        </Grid>

        {/* Right Panel - Form */}
        <Grid item xs={12} md={8}>
          {selectedObjective ? (
            <Paper sx={{
              p: 3,
              backgroundColor: selectedObjective.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
              borderRadius: 2
            }}>
              <Typography variant="h6" gutterBottom>
                Modifier l'Objectif
              </Typography>

              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                required
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Type d'objectif</InputLabel>
                <Select
                  value={objectiveType}
                  onChange={(e) => setObjectiveType(e.target.value as any)}
                  label="Type d'objectif"
                >
                  <MenuItem value="skill">Maîtriser un savoir-faire</MenuItem>
                  <MenuItem value="course">Savoir démontrer</MenuItem>
                  <MenuItem value="exercise_pattern">Savoir faire un exercice</MenuItem>
                </Select>
              </FormControl>

              {objectiveType === 'skill' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Savoir-faire</InputLabel>
                  <Select
                    value={skillId || ''}
                    onChange={(e) => setSkillId(e.target.value as number)}
                    label="Savoir-faire"
                  >
                    {skills.map(skill => (
                      <MenuItem key={skill.id} value={skill.id}>{skill.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {objectiveType === 'course' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Cours</InputLabel>
                  <Select
                    value={courseId || ''}
                    onChange={(e) => setCourseId(e.target.value as number)}
                    label="Cours"
                  >
                    {courses.map(course => (
                      <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {objectiveType === 'exercise_pattern' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Pattern d'exercice</InputLabel>
                  <Select
                    value={exercisePatternId || ''}
                    onChange={(e) => setExercisePatternId(e.target.value as number)}
                    label="Pattern d'exercice"
                  >
                    {exercisePatterns.map(pattern => (
                      <MenuItem key={pattern.id} value={pattern.id}>{pattern.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="Poids"
                type="number"
                value={weight}
                onChange={(e) => setWeight(Math.max(1, parseInt(e.target.value) || 1))}
                margin="normal"
                helperText="Le poids est utilisé pour pondérer le calcul des moyennes"
                inputProps={{ min: 1 }}
              />

            </Paper>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body1" color="text.secondary">
                Sélectionnez un objectif pour le modifier ou créez-en un nouveau
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Creation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Nouveau</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={createType}
              exclusive
              onChange={(e, newValue) => newValue && setCreateType(newValue)}
              fullWidth
            >
              <ToggleButton value="container">
                <FolderIcon sx={{ mr: 1 }} />
                Contenant
              </ToggleButton>
              <ToggleButton value="objective">
                <ObjectiveIcon sx={{ mr: 1 }} />
                Nouvel objectif
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {createType === 'container' && (
            <Box>
              <TextField
                autoFocus
                margin="dense"
                label="Nom du contenant"
                fullWidth
                variant="outlined"
                value={newContainerTitle}
                onChange={(e) => setNewContainerTitle(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateContainer();
                  }
                }}
              />
              <TextField
                margin="normal"
                label="Poids"
                type="number"
                fullWidth
                variant="outlined"
                value={newContainerWeight}
                onChange={(e) => setNewContainerWeight(Math.max(1, parseInt(e.target.value) || 1))}
                helperText="Le poids est utilisé pour pondérer le calcul des moyennes"
                inputProps={{ min: 1 }}
              />
            </Box>
          )}
          {createType === 'objective' && (
            <Box>
              <FormControl fullWidth margin="normal">
                <InputLabel>Type d'objectif</InputLabel>
                <Select
                  autoFocus
                  value={newObjectiveType}
                  onChange={(e) => {
                    setNewObjectiveType(e.target.value as any);
                    // Reset selection when changing type
                    setNewObjectiveSkillId(null);
                    setNewObjectiveCourseId(null);
                    setNewObjectivePatternId(null);
                    setAutoGeneratedTitle(true);
                  }}
                  label="Type d'objectif"
                >
                  <MenuItem value="skill">Maîtriser un savoir-faire</MenuItem>
                  <MenuItem value="course">Savoir démontrer</MenuItem>
                  <MenuItem value="exercise_pattern">Savoir faire un exercice</MenuItem>
                </Select>
              </FormControl>

              {newObjectiveType === 'skill' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Savoir-faire</InputLabel>
                  <Select
                    value={newObjectiveSkillId || ''}
                    onChange={(e) => setNewObjectiveSkillId(e.target.value as number)}
                    label="Savoir-faire"
                  >
                    {skills.map(skill => (
                      <MenuItem key={skill.id} value={skill.id}>{skill.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {newObjectiveType === 'course' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Cours</InputLabel>
                  <Select
                    value={newObjectiveCourseId || ''}
                    onChange={(e) => setNewObjectiveCourseId(e.target.value as number)}
                    label="Cours"
                  >
                    {courses.map(course => (
                      <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {newObjectiveType === 'exercise_pattern' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Pattern d'exercice</InputLabel>
                  <Select
                    value={newObjectivePatternId || ''}
                    onChange={(e) => setNewObjectivePatternId(e.target.value as number)}
                    label="Pattern d'exercice"
                  >
                    {exercisePatterns.map(pattern => (
                      <MenuItem key={pattern.id} value={pattern.id}>{pattern.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                margin="normal"
                label="Titre de l'objectif"
                fullWidth
                variant="outlined"
                value={newObjectiveTitle}
                onChange={(e) => {
                  setNewObjectiveTitle(e.target.value);
                  // Disable auto-generation when user manually edits
                  setAutoGeneratedTitle(false);
                }}
                helperText="Le titre est généré automatiquement, mais vous pouvez le modifier"
              />

              <TextField
                margin="normal"
                label="Poids"
                type="number"
                fullWidth
                variant="outlined"
                value={newObjectiveWeight}
                onChange={(e) => setNewObjectiveWeight(Math.max(1, parseInt(e.target.value) || 1))}
                helperText="Le poids est utilisé pour pondérer le calcul des moyennes"
                inputProps={{ min: 1 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setNewContainerTitle('');
            setNewContainerWeight(1);
            setNewObjectiveTitle('');
            setNewObjectiveType('skill');
            setNewObjectiveSkillId(null);
            setNewObjectiveCourseId(null);
            setNewObjectivePatternId(null);
            setNewObjectiveWeight(1);
            setAutoGeneratedTitle(true);
          }}>Annuler</Button>
          {createType === 'container' && (
            <Button variant="contained" onClick={handleCreateContainer}>Créer</Button>
          )}
          {createType === 'objective' && (
            <Button variant="contained" onClick={handleCreateNewObjective}>Créer</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Container Dialog */}
      <Dialog open={editContainerDialogOpen} onClose={() => setEditContainerDialogOpen(false)}>
        <DialogTitle>Modifier le contenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du contenant"
            fullWidth
            variant="outlined"
            value={editContainerTitle}
            onChange={(e) => setEditContainerTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editContainerTitle.trim()) {
                handleUpdateContainer();
              }
            }}
          />
          <TextField
            margin="normal"
            label="Description"
            placeholder="L'intérêt du bloc d'objectif (servira à la communication avec les parents)"
            fullWidth
            variant="outlined"
            value={editContainerDescription}
            onChange={(e) => setEditContainerDescription(e.target.value)}
            multiline
            rows={3}
          />
          <TextField
            margin="normal"
            label="Poids"
            type="number"
            fullWidth
            variant="outlined"
            value={editContainerWeight}
            onChange={(e) => setEditContainerWeight(Math.max(1, parseInt(e.target.value) || 1))}
            helperText="Le poids est utilisé pour pondérer le calcul des moyennes"
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditContainerDialogOpen(false);
            setEditingContainer(null);
            setEditContainerTitle('');
            setEditContainerDescription('');
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateContainer}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Objective Dialog */}
      <Dialog open={editObjectiveDialogOpen} onClose={() => setEditObjectiveDialogOpen(false)}>
        <DialogTitle>Modifier l'objectif</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Titre de l'objectif"
            fullWidth
            variant="outlined"
            value={editObjectiveTitle}
            onChange={(e) => setEditObjectiveTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editObjectiveTitle.trim()) {
                handleUpdateObjectiveTitle();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditObjectiveDialogOpen(false);
            setEditingObjective(null);
            setEditObjectiveTitle('');
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateObjectiveTitle}>Modifier</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Objectives;
